// @ts-nocheck
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { parseLocalDate } from '@/utils/formatters';

export interface CalendarEvent {
  id: string;
  profile_id: string | null;
  title: string;
  description: string | null;
  category: string;
  sport_id: string | null;
  start_date: string;
  end_date: string | null;
  is_custom: boolean;
  created_at: string;
  division: string | null;
  source: string | null;
  related_entity_id: string | null;
}

// Define valid calendar categories based on the database constraint
export type CalendarCategory = 
  | "ncaa_contact" 
  | "ncaa_dead" 
  | "ncaa_evaluation" 
  | "ncaa_quiet" 
  | "fafsa" 
  | "testing" 
  | "showcase" 
  | "milestone" 
  | "camp" 
  | "personal"
  | "other";

export type CalendarSource =
  | "ncaa"
  | "fafsa"
  | "testing"
  | "tracker"
  | "camp"
  | "saved_colleges"
  | "timeline"
  | "personal"
  | "custom";

export interface CalendarEventFormValues {
  title: string;
  description?: string;
  category: CalendarCategory;
  start_date: Date;
  end_date?: Date;
  division?: string;
  source?: CalendarSource;
  related_entity_id?: string;
}

// Helper function to validate category values
const isValidCategory = (category: string): boolean => {
  const validCategories: CalendarCategory[] = [
    "ncaa_contact",
    "ncaa_dead", 
    "ncaa_evaluation", 
    "ncaa_quiet", 
    "fafsa", 
    "testing", 
    "showcase", 
    "milestone", 
    "camp", 
    "personal",
    "other"
  ];
  
  return validCategories.includes(category as CalendarCategory);
};

export function useCalendarEvents() {
  const { profile } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [sportFilter, setSportFilter] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [divisionFilter, setDivisionFilter] = useState<string | null>(null);
  const [sourceFilter, setSourceFilter] = useState<string | null>(null);
  const [dateRangeFilter, setDateRangeFilter] = useState<{start?: Date, end?: Date} | null>(null);
  const { preferences } = useUserPreferences();

  const fetchEvents = async () => {
    if (!profile) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      // Updated query to include user events and admin events, but exclude NCAA events
      // NCAA events are handled separately by useNcaaCalendarEvents hook with sport filtering
      let query = supabase
        .from('calendar_events')
        .select('*')
        .or(`profile_id.eq.${profile.id},and(profile_id.is.null,source.neq.ncaa,category.neq.ncaa_contact,category.neq.ncaa_dead,category.neq.ncaa_evaluation,category.neq.ncaa_quiet)`);
        
      // Apply filters
      if (sportFilter) {
        query = query.eq('sport_id', sportFilter);
      }
      
      if (categoryFilter) {
        query = query.eq('category', categoryFilter);
      }
      
      if (divisionFilter) {
        query = query.eq('division', divisionFilter);
      }
      
      if (sourceFilter) {
        query = query.eq('source', sourceFilter);
      }
      
      if (dateRangeFilter?.start) {
        query = query.gte('start_date', dateRangeFilter.start.toISOString().split('T')[0]);
      }
      
      if (dateRangeFilter?.end) {
        query = query.lte('end_date', dateRangeFilter.end.toISOString().split('T')[0]);
      }

      const { data, error } = await query;
      
      if (error) {
        console.error("Error fetching calendar events:", error);
        setError("Failed to load calendar events");
        toast.error("Failed to load calendar events");
        return;
      }

      // Apply preference-based filtering to the events
      let filteredEvents = data || [];
      
      // Apply user preferences to filter out events from sources they've disabled
      if (preferences) {
        filteredEvents = filteredEvents.filter(event => {
          // For tracker tasks
          if (event.source === 'tracker' && preferences.calendarSyncTrackerTasks === false) {
            return false;
          }
          
          // For saved colleges
          if (event.source === 'saved_colleges' && preferences.calendarSyncSavedColleges === false) {
            return false;
          }
          
          // For college camps
          if (event.source === 'camp' && preferences.calendarSyncCollegeCamps === false) {
            return false;
          }
          
          // Timeline events are always shown since we manage them explicitly
          
          return true;
        });
      }

      setEvents(filteredEvents);
    } catch (error) {
      console.error("Error in calendar events hook:", error);
      setError("Something went wrong while loading calendar data");
      toast.error("Something went wrong while loading calendar data");
    } finally {
      setIsLoading(false);
    }
  };

  const addEvent = async (event: CalendarEventFormValues) => {
    if (!profile?.id) {
      console.error("Cannot add event: No profile found");
      toast.error("You must be logged in to add events");
      throw new Error("No profile ID available");
    }
    
    // Validate required fields
    const validationErrors = [];
    
    if (!event.title?.trim()) {
      validationErrors.push('title');
    }
    
    if (!event.start_date) {
      validationErrors.push('start date');
    }
    
    if (!event.category?.trim()) {
      validationErrors.push('category');
    }
    
    if (validationErrors.length > 0) {
      const errorMessage = `Missing required fields: ${validationErrors.join(', ')}`;
      console.error(errorMessage);
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }

    // Validate category against allowed values
    if (!isValidCategory(event.category)) {
      const errorMessage = `Invalid category: ${event.category}. Using 'personal' instead.`;
      console.warn(errorMessage);
      toast.warning(errorMessage);
      event.category = 'personal'; // Default to a safe value
    }
    
    try {
      // Format dates to YYYY-MM-DD format
      const startDateStr = event.start_date.toISOString().split('T')[0];
      const endDateStr = event.end_date ? event.end_date.toISOString().split('T')[0] : null;
      
      // Prepare the insert payload
      const eventData = {
        profile_id: profile.id,
        title: event.title.trim(),
        description: event.description?.trim() || null,
        category: event.category.trim(),
        start_date: startDateStr,
        end_date: endDateStr,
        is_custom: event.source === 'custom' || !event.source,
        division: event.division || null,
        sport_id: profile.sport_id || null,
        source: event.source?.trim() || 'personal',
        related_entity_id: event.related_entity_id || null
      };
      
      // Log the payload before insertion for debugging
      console.log("Inserting calendar event with payload:", eventData);
      
      // Perform the insert operation
      const { data, error } = await supabase
        .from('calendar_events')
        .insert(eventData)
        .select();
      
      if (error) {
        console.error("Error adding calendar event:", error);
        toast.error("Failed to add event: " + error.message);
        throw error;
      }
      
      console.log("Calendar event added successfully:", data);
      toast.success("✅ Event added to calendar");
      return data;
    } catch (error: any) {
      console.error("Exception adding calendar event:", error);
      toast.error(`Failed to add event: ${error?.message || 'Unknown error'}`);
      throw error;
    }
  };

  const deleteEvent = async (eventId: string): Promise<boolean> => {
    try {
      console.log("Deleting calendar event with ID:", eventId);
      
      const { error, data } = await supabase
        .from('calendar_events')
        .delete()
        .eq('id', eventId)
        .select();
      
      if (error) {
        console.error("Error deleting calendar event:", error);
        toast.error("Failed to delete event");
        return false;
      }
      
      console.log("Event deleted successfully:", data);
      toast.success("Event deleted successfully");
      fetchEvents();
      return true;
    } catch (error) {
      console.error("Error deleting calendar event:", error);
      toast.error("Something went wrong while deleting your event");
      return false;
    }
  };

  // Get category color for styling events
  const getCategoryColor = (category: string) => {
    const categoryColors: Record<string, string> = {
      ncaa_contact: '#22c55e', // green
      ncaa_dead: '#ef4444', // red
      ncaa_evaluation: '#f97316', // orange 
      ncaa_quiet: '#a855f7', // purple
      fafsa: '#0ea5e9', // sky blue
      testing: '#f59e0b', // amber
      showcase: '#6366f1', // indigo
      milestone: '#0891b2', // cyan
      camp: '#84cc16', // lime
      personal: '#38bdf8', // light blue
      other: '#6b7280', // gray
    };
    
    return categoryColors[category] || '#6b7280';
  };

  // Get source color for styling events
  const getSourceColor = (source: string | null, category: string) => {
    if (!source) return getCategoryColor(category);
    
    const sourceColors: Record<string, string> = {
      tracker: '#9b87f5', // purple (for recruiting tracker)
      camp: '#f97316',    // orange
      ncaa: getCategoryColor(category), // use category color for NCAA events
      fafsa: '#a855f7',   // purple
      testing: '#d946ef', // pink
      saved_colleges: '#3b82f6', // blue
      timeline: '#10b981', // emerald/green
      custom: '#0891b2',  // cyan
    };
    
    return sourceColors[source] || '#6b7280';
  };

  // Format events for calendar display
  const getFormattedEvents = () => {
    return events.map(event => ({
      id: event.id,
      title: event.title,
      start: event.start_date,
      end: event.end_date || event.start_date,
      backgroundColor: getSourceColor(event.source, event.category),
      borderColor: getSourceColor(event.source, event.category),
      extendedProps: {
        description: event.description,
        category: event.category,
        division: event.division,
        isCustom: event.is_custom,
        source: event.source,
        related_entity_id: event.related_entity_id
      }
    }));
  };

  // Generate ICS file for a specific event
  const generateEventICS = (event: CalendarEvent) => {
    // Basic ICS file format implementation
    let icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//RecruitLook//Calendar//EN',
      'BEGIN:VEVENT',
      `UID:${event.id}@recruitlook.com`,
      `DTSTAMP:${new Date().toISOString().replace(/[-:.]/g, '')}`,
      `DTSTART:${parseLocalDate(event.start_date).toISOString().replace(/[-:.]/g, '').split('T')[0]}`,
    ];
    
    if (event.end_date) {
      icsContent.push(`DTEND:${parseLocalDate(event.end_date).toISOString().replace(/[-:.]/g, '').split('T')[0]}`);
    }
    
    icsContent = [
      ...icsContent,
      `SUMMARY:${event.title}`,
      `DESCRIPTION:${event.description || ''}`,
      `CATEGORIES:${event.category}`,
      'END:VEVENT',
      'END:VCALENDAR'
    ];
    
    const blob = new Blob([icsContent.join('\r\n')], { type: 'text/calendar;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${event.title.replace(/[^a-z0-9]/gi, '_')}.ics`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    fetchEvents();
  }, [
    profile?.id, 
    sportFilter, 
    categoryFilter, 
    divisionFilter, 
    sourceFilter, 
    dateRangeFilter,
    // Add dependencies on the user preferences
    preferences?.calendarSyncTrackerTasks,
    preferences?.calendarSyncSavedColleges,
    preferences?.calendarSyncCollegeCamps
  ]);

  return {
    events,
    formattedEvents: getFormattedEvents(),
    isLoading,
    error,
    fetchEvents,
    addEvent,
    deleteEvent,
    generateEventICS,
    filters: {
      sportFilter,
      categoryFilter,
      divisionFilter,
      sourceFilter,
      dateRangeFilter,
      setSportFilter,
      setCategoryFilter,
      setDivisionFilter,
      setSourceFilter,
      setDateRangeFilter
    },
    getCategoryColor,
    getSourceColor
  };
}
